import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Indicateur de chargement global CENTRÉ avec progression 0→100 %.
 *
 * S'active automatiquement à chaque requête axios sortante et se masque
 * quand toutes les requêtes en cours sont terminées. Non-bloquant
 * par défaut (pointerEvents: none) : l'utilisateur garde la main sur la page.
 *
 * Comportement progression :
 *   - 1ère requête  → démarre à 15 %, monte rapidement à 70 %
 *   - puis avance lentement par paliers vers 95 % tant qu'il reste des req.
 *   - toutes terminées → 100 %, courte pause visible puis fade-out
 *
 * Sécurités anti-blocage (P0 — éviter le bug "reste à 95 % faut refresh") :
 *   - WATCHDOG : si rien ne change pendant 12 s alors que des req. semblent
 *     en cours, on force le reset (les requêtes étaient mortes silencieusement).
 *   - ROUTE CHANGE : à chaque changement d'URL (navigation), on flush le
 *     compteur (les requêtes pendantes appartiennent à l'ancienne page).
 *   - CLIC POUR FERMER : la barre est cliquable et l'utilisateur peut la
 *     masquer manuellement en cas d'attente prolongée.
 *
 * Visuel : carte glassmorphique centrée sur fond émeraude (charte Auto-Gère).
 */
const GlobalLoadingBar = () => {
  const { t } = useLanguage();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const pendingRef = useRef(0);
  const tickerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const watchdogRef = useRef(null);

  // Réinitialise complètement l'état (utilisé par watchdog + clic + route change).
  const forceReset = React.useCallback(() => {
    pendingRef.current = 0;
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
    setProgress(100);
    setTimeout(() => setVisible(false), 100);
    setTimeout(() => setProgress(0), 350);
  }, []);

  useEffect(() => {
    const startTicker = () => {
      if (tickerRef.current) return;
      tickerRef.current = setInterval(() => {
        setProgress(p => {
          if (p < 50) return p + 10;       // démarrage très rapide
          if (p < 80) return p + 3;        // accélération mi-parcours
          if (p < 95) return p + 0.5;      // ralentit en s'approchant
          return p;                         // plafond 95 % tant qu'il reste des req.
        });
      }, 200);
    };
    const stopTicker = () => {
      if (tickerRef.current) {
        clearInterval(tickerRef.current);
        tickerRef.current = null;
      }
    };

    const onStart = () => {
      pendingRef.current += 1;
      lastActivityRef.current = Date.now();
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setVisible(true);
      if (pendingRef.current === 1) {
        setProgress(15);
      }
      startTicker();
    };

    const onEnd = () => {
      pendingRef.current = Math.max(0, pendingRef.current - 1);
      lastActivityRef.current = Date.now();
      if (pendingRef.current === 0) {
        stopTicker();
        setProgress(100);
        hideTimerRef.current = setTimeout(() => {
          setVisible(false);
          setTimeout(() => setProgress(0), 250);
        }, 350);
      }
    };

    const reqInt = axios.interceptors.request.use(
      (cfg) => {
        // Requêtes de fond silencieuses (polls périodiques) : pas de barre.
        if (cfg?.headers?.['X-Silent-Request'] === '1') { cfg._silent = true; return cfg; }
        onStart();
        return cfg;
      },
      (err)  => { onEnd();   return Promise.reject(err); }
    );
    const resInt = axios.interceptors.response.use(
      (res) => { if (!res?.config?._silent) onEnd(); return res; },
      (err) => { if (!err?.config?._silent) onEnd(); return Promise.reject(err); }
    );

    // WATCHDOG anti-blocage : si rien ne change pendant 12 s, on présume
    // que les requêtes en attente sont mortes (CORS, abort, navigateur) et
    // on force le reset pour libérer l'UI.
    watchdogRef.current = setInterval(() => {
      if (pendingRef.current > 0 && (Date.now() - lastActivityRef.current) > 12000) {
        console.warn('[GlobalLoadingBar] Watchdog : requêtes silencieusement perdues, reset forcé.');
        forceReset();
      }
    }, 2000);

    // ROUTE CHANGE : sur popstate (navigation back/forward) et sur les
    // pushState/replaceState (React Router), on flush l'état. Idempotent
    // si rien n'est en cours (aucun re-render visible).
    const onRouteChange = () => {
      if (pendingRef.current > 0) {
        forceReset();
      }
    };
    window.addEventListener('popstate', onRouteChange);
    // Monkey-patch pushState/replaceState pour détecter aussi les navigations React Router.
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      const r = origPush.apply(this, args);
      onRouteChange();
      return r;
    };
    window.history.replaceState = function (...args) {
      const r = origReplace.apply(this, args);
      onRouteChange();
      return r;
    };

    return () => {
      axios.interceptors.request.eject(reqInt);
      axios.interceptors.response.eject(resInt);
      stopTicker();
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (watchdogRef.current) clearInterval(watchdogRef.current);
      window.removeEventListener('popstate', onRouteChange);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, [forceReset]);

  // ===== Rendu =====
  // Anneau SVG circulaire 80 px de diamètre + pourcentage au centre,
  // dans une carte glassmorphique brune/émeraude. Centré au milieu absolu
  // de l'écran, non-bloquant.
  const RADIUS = 36;                              // rayon de l'anneau
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;     // ≈ 226.19
  const dashOffset = CIRCUMFERENCE * (1 - progress / 100);

  return (
    <div
      data-testid="global-loading-bar"
      aria-hidden={!visible}
      aria-live="polite"
      role="status"
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) scale(${visible ? 1 : 0.85})`,
        opacity: visible ? 1 : 0,
        transition: 'opacity 200ms ease-out, transform 200ms ease-out',
        zIndex: 9999,
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <button
        type="button"
        data-testid="global-loading-dismiss"
        onClick={forceReset}
        title={t('common.clickToHide')}
        style={{
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid color-mix(in srgb, var(--accent-700, #047857) 25%, transparent)',
          borderRadius: '16px',
          padding: '20px 24px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(255,255,255,0.4) inset',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          minWidth: '180px',
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        {/* Anneau de progression */}
        <div style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
          <svg width="80" height="80" viewBox="0 0 80 80" style={{ transform: 'rotate(-90deg)' }}>
            {/* Cercle de fond */}
            <circle
              cx="40"
              cy="40"
              r={RADIUS}
              fill="none"
              stroke="color-mix(in srgb, var(--accent-700, #047857) 12%, transparent)"
              strokeWidth="6"
            />
            {/* Cercle de progression */}
            <circle
              cx="40"
              cy="40"
              r={RADIUS}
              fill="none"
              stroke="var(--accent-700, #047857)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 200ms ease-out' }}
            />
          </svg>
          {/* Pourcentage au centre */}
          <div
            data-testid="global-loading-percent"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 700,
              color: 'var(--accent-800, #065f46)',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.02em',
            }}
          >
            {Math.round(progress)}%
          </div>
        </div>
        {/* Libellé */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          <span
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--accent-800, #065f46)',
            }}
          >
            {t('common.loading')}
          </span>
          <span
            style={{
              fontSize: '12px',
              color: 'rgba(0, 0, 0, 0.55)',
            }}
          >
            {t('common.clickToHide')}
          </span>
        </div>
      </button>
    </div>
  );
};

export default GlobalLoadingBar;
